import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import PageHeader from 'components/UI/PageHeader/PageHeader'
import SearchBooks from 'components/Modules/Ebook/Books/components/SearchBooks/SearchBooks'
import FilterTextValues from 'components/UI/AsideFilter/FilterTextValues'
import AsideFilter from 'components/UI/AsideFilter/AsideFilter'
import st from './Analysis.module.scss'
import useLocalStorageFilter from 'helpers/useLocalStorageFilter'
import AsideFilterStore from 'components/UI/AsideFilter/asideFilterStore'
import useLocalization from 'hooks/useLocalization'
import { superAxiosRequest } from 'axiosApi'
import { useHistory } from 'react-router-dom'
import PercentPage from './PercentPage'
import FunnelAnalyticsBlock from './FunnelAnalyticsBlock'

const dateAfter = new Date()
const firstDayOfLastMoth = new Date(
  dateAfter.getFullYear(),
  dateAfter.getMonth(),
  1,
)
const lastDayOfLastMoth = new Date(
  dateAfter.getFullYear(),
  dateAfter.getMonth() + 1,
  0,
)

const SalesAnalysis = () => {
  const {
    isOpenFilter,
    closeFilter,
    setTextValues,
    changeTextValue,
    filterTextValues,
  } = AsideFilterStore
  const [filter, updateFilter] = useLocalStorageFilter({})
  const history = useHistory()

  const [salesAnalytics, setSalesAnalytics] = useState([])
  const [selectedPage, setSelectedPage] = useState(0)
  const [columns, setColumns] = useState([])
  const [maxQuantity, setMaxQuantity] = useState(null)
  const [funnels, setFunnels] = useState([])
  const [maxBudget, setMaxBudget] = useState(null)
  const [loading, setLoading] = useState(false)

  const [funnelStatusData, setFunnelStatusData] = useState(null)
  const [funnelStatusLoading, setFunnelStatusLoading] = useState(false)

  const { translate } = useLocalization({
    section: 'crm',
    subsection: 'sales_analysis',
    dict: {
      filter: 'Filter',
      search: 'Search',
      start_date: 'Start Date',
      end_date: 'End Date',
      reset_all: 'Reset All',
      apply: 'Apply',
      sales_analysis: 'Sales analysis',
      search_placeholder: 'Search',
      new_block: 'New',
      won_block: 'Won',
      average_trade_duration: 'Average trade duration',
      all_tab: 'All',
      active_tab: 'Active',
      closed_tab: 'Closed',
      by_budget_tab: 'By budget',
      by_quantity_tab: 'By quantity',
      transactions_field: 'Transactions',
      start_finish_date: 'Start & Finish Date',
      funnel: 'Funnel',
      deal_status: 'Deal status',
      budget: 'Budget',
    },
  })

  const tabsBtn = [
    { id: 0, name: translate?.all_tab },
    { id: 1, name: translate?.active_tab },
    { id: 2, name: translate?.closed_tab },
  ]

  const config = [
    {
      nameState: ['date_from', 'date_until'],
      type: 'date_day_picker',
      defaultDate: {
        after: firstDayOfLastMoth,
        before: lastDayOfLastMoth,
      },
      placeholder: translate?.start_finish_date,
      label: translate?.start_finish_date,
    },
    {
      nameState: 'funnel_id',
      placeholder: translate.funnel,
      values: funnels,
      type: 'select',
      label: translate.funnel,
      isRequired: true,
    },
    // {
    //   nameState: 'status_deal',
    //   placeholder: translate.deal_status,
    //   values: columns.map((col) => ({ id: col.id, name: col.name })),
    //   type: 'select-multi',
    //   label: translate.deal_status,
    // },
    // {
    //   nameState: 'budget',
    //   placeholder: translate.budget,
    //   type: 'input',
    //   label: translate.budget,
    // },
  ]

  const [averageDurationData, setAverageDurationData] = useState([])
  const [averageDurationLoading, setAverageDurationLoading] = useState(false)

  // Добавляем функцию для получения данных о длительности
  const fetchAverageDuration = () => {
    if (!filter?.date_from || !filter?.date_until || !filter?.funnel_id) {
      return Promise.resolve(null)
    }

    setAverageDurationLoading(true)
    return superAxiosRequest({
      method: 'get',
      link: 'reports/avg_deals_durations',
      crm: true,
      params: {
        date_from: filter.date_from,
        date_until: filter.date_until,
        funnel_id: filter.funnel_id,
      },
    })
      .then(({ data }) => {
        setAverageDurationData(data?.avg_deals_durations || '')
      })
      .catch((error) => {
        console.error('Error fetching average duration:', error)
        setAverageDurationData([])
        return []
      })
      .finally(() => {
        setAverageDurationLoading(false)
      })
  }

  const handleDropFilter = () => {
    const now = new Date()
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(now.getDate() - 30)

    updateFilter({
      date_from: thirtyDaysAgo.toISOString().split('T')[0],
      date_until: now.toISOString().split('T')[0],
    })
    closeFilter()
  }

  const mergeStatusesWithAnalytics = (statuses, analytics) => {
    return statuses.map((status) => ({
      ...status,
      deals_count: analytics[status.id]?.deals_count || 0,
      deals_ids: analytics[status.id]?.deals_ids || [],
      avg_days: analytics[status.id]?.avg_days || 0,
      budget: analytics[status.id]?.budget || 0,
    }))
  }

  const handlePageChange = (id) => {
    setSelectedPage(id)
  }

  const fetchDealStatuses = (funnelId) => {
    return superAxiosRequest({
      method: 'get',
      link: 'integration/deal_statuses',
      crm: true,
      params: {
        funnel_id: funnelId,
      },
    })
      .then((response) => {
        const statusesArray = Object.values(response.data).sort(
          (a, b) => a.position - b.position,
        )
        setColumns(statusesArray)
        return statusesArray
      })
      .catch((error) => {
        console.error('Error fetching deal statuses:', error)
        return []
      })
  }

  const fetchSalesAnalytics = () => {
    const params = {
      date_from: filter?.date_from,
      date_until: filter?.date_until,
      funnel_id: filter?.funnel_id,
    }

    if (filter?.status_deal?.length) {
      params.status_deal = filter.status_deal.join(',')
    }

    if (filter?.budget) {
      params.budget = Number(filter?.budget)
    }

    return superAxiosRequest({
      method: 'get',
      link: 'reports/sales_analytics',
      crm: true,
      params,
    })
      .then(({ data }) => {
        return data || {}
      })
      .catch((error) => {
        console.error('Error fetching sales analytics:', error)
        return {}
      })
  }

  const fetchFunnelStatusAnalytics = () => {
    if (!filter?.date_from || !filter?.date_until || !filter?.funnel_id) {
      return Promise.resolve(null)
    }

    setFunnelStatusLoading(true)
    return superAxiosRequest({
      method: 'get',
      link: 'reports/get_analytics_deals_statuses',
      crm: true,
      params: {
        date_from: filter.date_from,
        date_until: filter.date_until,
        funnel_id: filter.funnel_id,
      },
    })
      .then(({ data }) => {
        setFunnelStatusData(data || null)
        return data || null
      })
      .catch((error) => {
        console.error('Error fetching funnel status analytics:', error)
        setFunnelStatusData(null)
        return null
      })
      .finally(() => {
        setFunnelStatusLoading(false)
      })
  }

  const loadData = async () => {
    setLoading(true)

    try {
      const statuses = await fetchDealStatuses(filter?.funnel_id)
      const analytics = await fetchSalesAnalytics()
      await fetchFunnelStatusAnalytics()
      await fetchAverageDuration()

      const mergedData = mergeStatusesWithAnalytics(statuses, analytics)

      const maxDealsQuantity = Math.max(
        ...mergedData.map((item) => item?.deals_count || 0),
        0,
      )
      setMaxQuantity(maxDealsQuantity)

      const maxBudgetValue = Math.max(
        ...mergedData.map((item) => item?.budget || 0),
        0,
      )
      setMaxBudget(maxBudgetValue)

      setSalesAnalytics(mergedData)
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    superAxiosRequest({
      method: 'get',
      link: 'integration/funnels',
      crm: true,
    })
      .then(({ data }) => {
        setFunnels(data)
      })
      .catch((error) => {
        console.error('Error fetching funnels:', error)
      })
  }, [])

  useEffect(() => {
    if (!filter.date_from && !filter.date_until) {
      const now = new Date()
      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(now.getDate() - 30)

      updateFilter({
        ...filter,
        date_from: thirtyDaysAgo.toISOString().split('T')[0],
        date_until: now.toISOString().split('T')[0],
      })
    }
  }, [])

  useEffect(() => {
    if (filter?.funnel_id && filter?.date_from && filter?.date_until) {
      loadData()
    }
  }, [
    filter?.funnel_id,
    filter?.date_from,
    filter?.date_until,
    filter?.status_deal,
    filter?.budget,
    selectedPage,
  ])

  useEffect(() => {
    return AsideFilterStore.unmountFilter
  }, [])

  useEffect(() => {
    const unlisten = history.listen((location) => {
      if (location.pathname !== '/crm/sales-analyst') {
        updateFilter({})
      }
    })
    return () => {
      unlisten()
    }
  }, [history, updateFilter])

  return (
    <div>
      <div className={st.header}>
        <div>
          <PageHeader title={translate?.sales_analysis}></PageHeader>
          <SearchBooks
            onSearch={(text) => {
              updateFilter({ ...filter, name: text })
              changeTextValue('name', {
                key: 'name',
                label: 'Search',
                value: text,
              })
            }}
            onClear={() => {
              const { name: text, ...obj } = filter
              updateFilter(obj)
              setTextValues(
                filterTextValues.filter((field) => field.key !== 'name'),
              )
            }}
            value={filter?.name || ''}
            placeholder={`${translate?.search}...`}
            translate={translate}
          />
        </div>
        {!!filterTextValues.length && (
          <FilterTextValues
            onDelete={(key) => {
              const { [key]: _, ...obj } = filter
              updateFilter(obj)
            }}
          />
        )}
        {isOpenFilter && (
          <AsideFilter
            getFilterParams={(e) => {
              updateFilter(e)
            }}
            config={config}
            filterValue={filter}
            onDropFilter={() => {
              handleDropFilter()
            }}
            translate={translate}
          />
        )}
      </div>
      {filter?.funnel_id ? (
        <>
          <FunnelAnalyticsBlock
            data={funnelStatusData}
            statuses={columns}
            loading={funnelStatusLoading}
            translate={translate}
          />
          <PercentPage
            loading={loading}
            maxQuantity={maxQuantity}
            maxBudget={maxBudget}
            salesAnalytics={salesAnalytics}
            handlePageChange={handlePageChange}
            pages={tabsBtn}
            selectedPage={selectedPage}
            translate={translate}
            averageDurationData={averageDurationData}
          />
        </>
      ) : (
        <div className={st.notice}>Нет данных для отображения!</div>
      )}
    </div>
  )
}

export default observer(SalesAnalysis)
